import { Avatar, CheckCircle, Mail, Phone } from '@chain-zen/ui'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import React, { useState } from 'react'

interface IUserInfoTooltipProps {
  children: React.ReactNode
  showTooltip?: boolean
  user: {
    email?: string
    id?: string
    name?: string
    online?: boolean
    phone?: string
    photo?: string | null
    userType?: string | null
  }
  workspace?: { id?: string; isVerified?: boolean; name?: string }
}

export const UserInfoTooltip = ({ children, showTooltip, user, workspace }: IUserInfoTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const { x, y, refs, context } = useFloating({
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['left', 'bottom', 'top'],
        padding: 8
      }),
      shift({
        crossAxis: true,
        mainAxis: true,
        padding: 8
      })
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  })

  const hover = useHover(context, {
    delay: { close: 160, open: 300 },
    handleClose: safePolygon({
      blockPointerEvents: true
    }),
    mouseOnly: true,
    move: false,
    restMs: 40
  })

  const click = useClick(context, {
    enabled: false
  })

  const { getFloatingProps, getReferenceProps } = useInteractions([hover, click])

  if (!showTooltip) {
    return <>{children}</>
  }

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      {!!isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              left: x ?? 0,
              position: 'fixed',
              top: y ?? 0,
              zIndex: 1000
            }}
            {...getFloatingProps()}
            className="w-72 overflow-hidden rounded-md border border-slate-200 bg-white p-0 shadow-md dark:border-zinc-800 dark:bg-zinc-900"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div className="w-full space-y-4 p-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar
                    src={user.photo || ''}
                    name={user.name || ''}
                    alt={user.name || ''}
                    className="h-12 w-12 border border-slate-100 dark:border-zinc-700"
                  />
                  {!!user.online && (
                    <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 ring-1 ring-white dark:ring-zinc-900" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="truncate text-base font-semibold text-slate-900 dark:text-white">{user.name}</h3>
                    {!!workspace?.isVerified && (
                      <CheckCircle className="h-4 w-4 fill-blue-100 text-blue-500 dark:fill-blue-900" />
                    )}
                  </div>
                  {!!user.online && (
                    <span className="mt-1 inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400">
                      <span className="mr-1.5 h-2 w-2 rounded-full bg-green-500" />
                      Online
                    </span>
                  )}
                </div>
              </div>

              {(!!user.email || !!user.phone) && (
                <div className="space-y-2">
                  {!!user.email && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  )}
                  {!!user.phone && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                </div>
              )}

              {!!workspace?.name && (
                <div className="mt-0.5 border-t border-slate-100 pt-3 dark:border-zinc-800">
                  <p className="mb-1 text-xs font-medium text-slate-900 dark:text-slate-200">Company</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{workspace.name}</p>
                </div>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
